import { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { NATUREZAS, nomeNatureza, br, isoLocal } from './LancamentoOcorrencias'

// Exclusão de Ocorrências (WOCOEXC/POCOEXC do ERP): seleciona várias e escolhe o que apagar

interface Linha { ococod: string; ocodat: string; ocotip: number; oconro: number; nome: string; tipo: string; oconat: string; ocosta: string; envolvidos: number; auditorias: number }
const chaveDe = (l: Linha) => `${l.ococod}|${l.ocodat}|${l.ocotip}|${l.oconro}`

export default function ExcluirOcorrencias() {
  const hoje = new Date()
  const [inicio, setInicio] = useState(isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1)))
  const [fim, setFim] = useState(isoLocal(hoje))
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [natureza, setNatureza] = useState('')
  const [status, setStatus] = useState('')
  const [lista, setLista] = useState<Linha[] | null>(null)
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set())
  // igual ao ERP: "Excluir tudo" começa marcado e trava as outras duas opções
  const [tudo, setTudo] = useState(true)
  const [envolvidos, setEnvolvidos] = useState(true)
  const [auditorias, setAuditorias] = useState(true)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [excluindo, setExcluindo] = useState(false)

  async function buscar() {
    setErro('')
    try {
      const r = await api.get('/ocorrencias-funcionarios/exclusao', { params: { inicio, fim, motcod: funcCod, natureza, status } })
      setLista(r.data)
      setMarcadas(new Set())
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao buscar as ocorrências.')
    }
  }
  useEffect(() => { buscar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function marcar(l: Linha) {
    setMarcadas(m => { const n = new Set(m); const k = chaveDe(l); if (n.has(k)) n.delete(k); else n.add(k); return n })
  }
  const todasMarcadas = !!lista?.length && lista.every(l => marcadas.has(chaveDe(l)))

  async function excluir() {
    setErro(''); setAviso('')
    const itens = (lista ?? []).filter(l => marcadas.has(chaveDe(l)))
    if (!itens.length) { setErro('Selecione ao menos uma ocorrência para exclusão!'); return }
    if (!tudo && !envolvidos && !auditorias) { setErro('Marque o que deve ser excluído.'); return }
    const oque = tudo ? 'as ocorrências inteiras (texto, envolvidos e auditorias)' : [envolvidos && 'os envolvidos', auditorias && 'as auditorias/encaminhamentos/acompanhamentos'].filter(Boolean).join(' e ')
    if (!window.confirm(`Confirma exclusão de ${oque} de ${itens.length} ocorrência(s) selecionada(s)?`)) return
    setExcluindo(true)
    try {
      const r = await api.post('/ocorrencias-funcionarios/exclusao', {
        tudo, envolvidos, auditorias, itens: itens.map(({ ococod, ocodat, ocotip, oconro }) => ({ ococod, ocodat, ocotip, oconro })),
      })
      const falhas = (r.data.resultado as { oconro: number; ok: boolean; erro?: string }[]).filter(x => !x.ok)
      if (falhas.length) setErro(`Não foi possível excluir: ${falhas.map(f => `nº ${f.oconro} (${f.erro})`).join('; ')}`)
      setAviso(`Concluído! ${itens.length - falhas.length} ocorrência(s) processada(s).`)
      await buscar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    } finally {
      setExcluindo(false)
    }
  }

  const th: React.CSSProperties = { padding: '0.45rem 0.5rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.5rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
  const opcao = (rot: string, val: boolean, set: (v: boolean) => void, travado = false) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', fontWeight: 600, color: travado ? '#9ca3af' : '#1f2937', cursor: travado ? 'not-allowed' : 'pointer' }}>
      <input type="checkbox" checked={val} disabled={travado} onChange={e => set(e.target.checked)} /> {rot}
    </label>
  )

  return (
    <div style={{ width: '100%', maxWidth: 1200 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Excluir Ocorrências
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="POCOEXC">WOCOEXC</span>
      </h2>
      <div className="card">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
          <div className="field">
            <label>Período</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} />
              <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>a</span>
              <input type="date" value={fim} onChange={e => setFim(e.target.value)} />
            </div>
          </div>
          <div className="field" style={{ minWidth: 260 }}>
            <label>Funcionário</label>
            <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
              codValue={funcCod} descValue={funcNome} onSelect={(c, d) => { setFuncCod(c); setFuncNome(d) }} placeholder="Todos" listWidth={320} />
          </div>
          <div className="field">
            <label>Natureza</label>
            <select value={natureza} onChange={e => setNatureza(e.target.value)}>
              <option value="">Todas</option>
              {NATUREZAS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">Todos</option>
              <option value="A">Em Andamento</option>
              <option value="C">Concluída</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={buscar}>Buscar</button>
          {funcCod && <button className="btn btn-outline" onClick={() => { setFuncCod(''); setFuncNome('') }}>Limpar funcionário</button>}
        </div>

        <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'center', padding: '0.7rem 1rem', marginBottom: '0.9rem',
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
          <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Excluir</span>
          {opcao('Tudo (a ocorrência inteira)', tudo, v => { setTudo(v); if (v) { setEnvolvidos(true); setAuditorias(true) } })}
          {opcao('Envolvidos do sinistro', envolvidos, setEnvolvidos, tudo)}
          {opcao('Auditorias / encaminhamentos / acompanhamentos', auditorias, setAuditorias, tudo)}
          <div style={{ flex: 1 }} />
          <button className="btn" disabled={excluindo || !marcadas.size} onClick={excluir}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#b91c1c', color: '#fff', border: '1px solid #991b1b' }}>
            <Trash2 size={15} /> {excluindo ? 'Excluindo...' : `Excluir selecionadas (${marcadas.size})`}
          </button>
        </div>

        {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}
        {aviso && <div style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem' }}>{aviso}</div>}
        {lista && lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma ocorrência no período.</p>}
        {lista && lista.length > 0 && (
          <div style={{ border: '1px solid #bfdbfe', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', maxWidth: 'none', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 40 }} /><col style={{ width: 90 }} /><col style={{ width: 70 }} /><col /><col style={{ width: 200 }} />
                <col style={{ width: 96 }} /><col style={{ width: 92 }} /><col style={{ width: 92 }} /><col style={{ width: 114 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'center' }}>
                    <input type="checkbox" checked={todasMarcadas} title="Marcar/desmarcar todas"
                      onChange={() => setMarcadas(todasMarcadas ? new Set() : new Set(lista.map(chaveDe)))} />
                  </th>
                  <th style={th}>Data</th><th style={th}>Número</th><th style={th}>Funcionário</th><th style={th}>Tipo</th>
                  <th style={th}>Natureza</th><th style={{ ...th, textAlign: 'center' }}>Envolvidos</th><th style={{ ...th, textAlign: 'center' }}>Auditorias</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((l, i) => {
                  const sel = marcadas.has(chaveDe(l))
                  return (
                    <tr key={chaveDe(l)} onClick={() => marcar(l)} style={{ background: sel ? '#fee2e2' : i % 2 ? '#f5f9fd' : '#fff', cursor: 'pointer' }}>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={sel} onChange={() => marcar(l)} onClick={e => e.stopPropagation()} /></td>
                      <td style={td}>{br(l.ocodat)}</td>
                      <td style={td}>{l.oconro}</td>
                      <td style={{ ...td, fontWeight: 600 }} title={`${l.ococod} - ${l.nome ?? ''}`}>{l.ococod ? `${l.ococod} - ${l.nome ?? ''}` : '—'}</td>
                      <td style={td} title={l.tipo}>{l.ocotip} - {l.tipo}</td>
                      <td style={td}>{nomeNatureza(l.oconat)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>{l.envolvidos || ''}</td>
                      <td style={{ ...td, textAlign: 'center' }}>{l.auditorias || ''}</td>
                      <td style={{ ...td, fontWeight: 600, color: l.ocosta === 'C' ? '#15803d' : '#b45309' }}>{l.ocosta === 'C' ? 'Concluída' : 'Em Andamento'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {lista && lista.length === 500 && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 6 }}>Mostrando as 500 mais recentes — refine o filtro.</div>}
      </div>
    </div>
  )
}
